import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Map, Check, Layers } from 'lucide-react';
import { useMapProvider, MAP_PROVIDERS } from '@/hooks/useMapProvider';
import { toast } from 'sonner';

export default function MapSelector({ compact = false, className = '' }) {
  const { provider, changeProvider } = useMapProvider();
  const [open, setOpen] = useState(false);

  const handleSelect = (key) => {
    changeProvider(key);
    setOpen(false);
    toast.success(`Mapa cambiado a ${MAP_PROVIDERS[key].name}`);
  };

  const currentProvider = MAP_PROVIDERS[provider];

  if (compact) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={`gap-1 bg-white/10 border-gray-500 text-white hover:bg-white/20 hover:text-white ${className}`}
            data-testid="map-selector-compact"
          >
            <Layers className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Estilo de mapa</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {Object.entries(MAP_PROVIDERS).map(([key, config]) => (
            <DropdownMenuItem
              key={key}
              onClick={() => handleSelect(key)}
              className="flex items-center justify-between cursor-pointer"
            >
              <div>
                <p className="font-medium">{config.name}</p>
                <p className="text-xs text-gray-500">{config.weight}</p>
              </div>
              {provider === key && <Check className="w-4 h-4 text-green-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className={`bg-white rounded-lg border p-3 ${className}`} data-testid="map-selector">
      <div className="flex items-center gap-2 mb-2">
        <Map className="w-4 h-4 text-orange-500" />
        <span className="font-medium text-sm">Estilo de mapa</span>
      </div>
      <div className="grid grid-cols-1 gap-1">
        {Object.entries(MAP_PROVIDERS).map(([key, config]) => (
          <button
            key={key}
            onClick={() => handleSelect(key)}
            className={`flex items-center justify-between p-2 rounded text-left text-sm transition-colors ${
              provider === key 
                ? 'bg-orange-100 border border-orange-300' 
                : 'hover:bg-gray-100 border border-transparent'
            }`}
          >
            <div className="flex-1 min-w-0">
              <p className={`font-medium truncate ${provider === key ? 'text-orange-700' : 'text-gray-800'}`}>
                {config.name}
              </p>
              <p className="text-xs text-gray-500 truncate">{config.description}</p>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <span className="text-xs text-gray-400">{config.weight}</span>
              {provider === key && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
